import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, 
  ChevronRight,
  Baby,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import {
  ChartBar,
  CurrencyCircleDollar,
  Users,
  ShoppingCart,
  Plus,
  CalendarCheck,
  Target,
  Trophy,
  ArrowRight,
  House,
  SignOut,
  Heartbeat,
  BookOpen,
  Question,
  X,
  Pill,
  Clock,
  CheckCircle,
  ArrowSquareOut,
  Package,
  Truck
} from '@phosphor-icons/react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { CureReminders } from '../components/cure';
import MyPerformanceCard from '../components/admin/AgentPerformance/MyPerformanceCard';
import MyScoringCard from '../components/staff/MyScoringCard';
import MyManagerObjectives from '../components/staff/MyManagerObjectives';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } }
};

// Liens utiles pour le personnel de la pharmacie
const usefulLinks = [
  { name: 'Vidal', url: 'https://www.vidal.fr', description: 'Base de données médicaments' },
  { name: 'AIRP CI', url: 'https://airp.ci', description: 'Autorité Ivoirienne de Régulation Pharmaceutique' },
  { name: 'CNOP CI', url: 'https://cnop.ci', description: 'Conseil National de l\'Ordre des Pharmaciens' },
  { name: 'Ubipharm', url: 'https://client-planetpharma.ubipharm.com/', description: 'Grossiste répartiteur' },
  { name: 'Copharmed', url: 'https://www.copharmed.com', description: 'Grossiste répartiteur' },
  { name: 'Tedis', url: 'https://www.tedispharma-ci.com/', description: 'Grossiste répartiteur' },
  { name: 'Exopharm', url: 'https://apps.apple.com/ci/app/strategix-mobile/id6504087788', description: 'App Exopharm (Strategix)' },
  { name: 'DPCI', url: 'https://pro2.dpci.ci/login', description: 'Direction de la Pharmacie de Côte d\'Ivoire' },
];

const AgentDashboard = ({ onNewDiagnosis, onExpressDiagnosis, onViewDiagnosis, onViewStats, onViewClients, onViewHelp, onBackToPublic, onLogout, onSwitchToHealth, onSwitchToRayons, onSwitchToReception, onBabyDiagnosis }) => {
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinks, setShowLinks] = useState(false);
  const [followUps, setFollowUps] = useState({ scheduled_follow_ups: [], renewal_reminders: [] });
  const [supplementRenewals, setSupplementRenewals] = useState({ renewals: [], total: 0, overdue_count: 0 });
  const [currentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear] = useState(new Date().getFullYear());

  const fetchData = React.useCallback(async () => {
    try {
      const [statsRes, diagnosesRes] = await Promise.all([
        axios.get(`${API_URL}/stats/agent/${user.id}?month=${currentMonth}&year=${currentYear}`),
        axios.get(`${API_URL}/diagnosis/history`)
      ]);
      setStats(statsRes.data);
      setDiagnoses(diagnosesRes.data);
    } catch (error) {
      console.error('Failed to fetch agent data:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id, currentMonth, currentYear]);

  const fetchFollowUps = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/to-follow-up`);
      setFollowUps(response.data);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
    }
  }, []);

  const fetchSupplementRenewals = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/supplements/renewals`);
      setSupplementRenewals(response.data);
    } catch (error) {
      console.error('Failed to fetch supplement renewals:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchFollowUps();
    fetchSupplementRenewals();
  }, [fetchData, fetchFollowUps, fetchSupplementRenewals]);

  const handleMarkRenewalDone = async (saleId, supplementId, clientName) => {
    try {
      await axios.put(`${API_URL}/supplements/renewals/${saleId}/${supplementId}/mark-done`);
      toast.success(language === 'fr' 
        ? `Relance pour ${clientName} marquée comme faite` 
        : `Follow-up for ${clientName} marked as done`);
      fetchSupplementRenewals();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const formatRenewalDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd MMM', { locale: language === 'fr' ? fr : enUS });
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM yyyy HH:mm', {
      locale: language === 'fr' ? fr : enUS
    });
  };

  const getConversionColor = (rate) => {
    if (rate >= 70) return 'text-green-600';
    if (rate >= 40) return 'text-yellow-600';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#2E7D32] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-28 sm:pb-32 relative overflow-x-hidden font-body">
      {/* Header - Premium Green */}
      <header className="relative z-10 bg-[#2E7D32]">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {onBackToPublic && (
                <button
                  onClick={onBackToPublic}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors text-white"
                  data-testid="back-to-public-dashboard"
                >
                  <House size={18} weight="fill" />
                  <span className="text-sm font-medium hidden sm:inline">Retour au site</span>
                </button>
              )}
              <Badge className="bg-white/20 text-white border-0 font-medium">
                SADASkinCare
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              {onSwitchToHealth && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 bg-blue-500/30 rounded-lg"
                  onClick={onSwitchToHealth}
                  data-testid="switch-to-health-btn"
                >
                  <Heartbeat size={18} weight="fill" className="mr-1" />
                  <span className="hidden sm:inline">SADAHealth</span>
                </Button>
              )}
              {onSwitchToRayons && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 bg-purple-500/30 rounded-lg"
                  onClick={onSwitchToRayons}
                  data-testid="switch-to-rayons-btn"
                  title="SADARayons — Inventaire & Stock"
                >
                  <Package size={18} weight="fill" className="mr-1" />
                  <span className="hidden sm:inline">SADARayons</span>
                </Button>
              )}
              {onSwitchToReception && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 bg-teal-500/30 rounded-lg"
                  onClick={onSwitchToReception}
                  data-testid="switch-to-reception-btn"
                  title="SADAGAS — Réception IA"
                >
                  <Truck size={18} weight="fill" className="mr-1" />
                  <span className="hidden sm:inline">SADAGAS</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 rounded-lg"
                onClick={() => setShowLinks(!showLinks)}
                data-testid="useful-links-btn"
              >
                <BookOpen size={18} weight="duotone" className="mr-1" />
                <span className="hidden sm:inline">{language === 'fr' ? 'Liens' : 'Links'}</span>
              </Button>
              {onViewHelp && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 rounded-lg"
                  onClick={onViewHelp}
                  data-testid="help-btn"
                >
                  <Question size={18} weight="duotone" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-red-500/80 bg-white/10 rounded-lg"
                onClick={() => {
                  logout();
                  if (onLogout) onLogout();
                }}
                data-testid="logout-btn"
              >
                <SignOut size={18} weight="duotone" className="mr-1" />
                <span className="hidden sm:inline">{language === 'fr' ? 'Quitter' : 'Logout'}</span>
              </Button>
            </div>
          </div>
          
          <h1 className="font-display text-2xl font-bold text-white mb-1">
            {language === 'fr' ? 'Bonjour' : 'Hello'}, {user?.name}
          </h1>
          <p className="text-white/80 text-sm">
            {language === 'fr' 
              ? `Statistiques de ${format(new Date(), 'MMMM yyyy', { locale: fr })}`
              : `Statistics for ${format(new Date(), 'MMMM yyyy', { locale: enUS })}`
            }
          </p>
        </div>
      </header>

      {/* Liens utiles Panel */}
      {showLinks && (
        <div className="relative z-20 bg-white shadow-lg border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen size={20} weight="duotone" className="text-[#2E7D32]" />
                {language === 'fr' ? 'Liens utiles' : 'Useful Links'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLinks(false)}
                className="text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X size={20} weight="bold" />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {usefulLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 bg-gray-50 hover:bg-[#E8F5E9] rounded-xl transition-colors group"
                >
                  <ArrowSquareOut size={18} weight="duotone" className="text-gray-400 group-hover:text-[#2E7D32] mb-2" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#2E7D32] text-center">{link.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10 max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards - Premium Design with Animations */}
        <motion.div 
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={cardVariants}>
            <Card className="border border-gray-100 bg-white shadow-card rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300" data-testid="diagnoses-stat">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Users size={24} weight="duotone" className="text-[#2E7D32]" />
                  </motion.div>
                </div>
                <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
                  {language === 'fr' ? 'Diagnostics' : 'Diagnoses'}
                </p>
                <motion.p 
                  className="font-display text-3xl font-bold text-gray-900 mt-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  {stats?.total_diagnoses || 0}
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={cardVariants}>
            <Card className="border border-gray-100 bg-white shadow-card rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300" data-testid="sales-stat">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <ShoppingCart size={24} weight="duotone" className="text-blue-600" />
                  </motion.div>
                </div>
                <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
                  {language === 'fr' ? 'Ventes' : 'Sales'}
                </p>
                <motion.p 
                  className="font-display text-3xl font-bold text-gray-900 mt-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.25, type: "spring", stiffness: 200 }}
                >
                  {stats?.total_sales || 0}
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={cardVariants}>
            <Card className="border border-gray-100 bg-white shadow-card rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300" data-testid="revenue-stat">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <CurrencyCircleDollar size={24} weight="duotone" className="text-amber-600" />
                  </motion.div>
                </div>
                <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
                  {language === 'fr' ? 'Chiffre d\'affaires' : 'Revenue'}
                </p>
                <motion.p 
                  className="font-display text-2xl font-bold text-gray-900 mt-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  {stats?.total_revenue?.toLocaleString('fr-FR') || 0} <span className="text-lg text-gray-500">FCFA</span>
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={cardVariants}>
            <Card className="border border-gray-100 bg-white shadow-card rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300" data-testid="conversion-stat">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Target size={24} weight="duotone" className="text-emerald-600" />
                  </motion.div>
                </div>
                <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
                  {language === 'fr' ? 'Taux conversion' : 'Conversion'}
                </p>
                <motion.p 
                  className={`font-display text-3xl font-bold mt-1 ${getConversionColor(stats?.conversion_rate || 0)}`}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.35, type: "spring", stiffness: 200 }}
                >
                  {stats?.conversion_rate?.toFixed(1) || 0}%
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Average Basket */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
        >
          <Card className="border border-gray-100 bg-white shadow-card rounded-2xl hover:shadow-card-hover transition-all duration-300" data-testid="basket-stat">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-14 h-14 rounded-2xl bg-[#2E7D32] flex items-center justify-center"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <ChartBar size={28} weight="duotone" className="text-white" />
                  </motion.div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
                      {language === 'fr' ? 'Panier moyen' : 'Average Basket'}
                    </p>
                    <p className="font-display text-2xl font-bold text-gray-900">
                      {stats?.average_basket?.toLocaleString('fr-FR') || 0} <span className="text-lg text-gray-500">FCFA</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className="bg-[#E8F5E9] text-[#2E7D32] font-medium">
                    {language === 'fr' ? 'Ce mois' : 'This month'}
                  </Badge>
                </div>
            </div>
          </CardContent>
        </Card>
        </motion.div>

        {/* Carte « Mes performances multi-modules » (Admin RH) */}
        <MyPerformanceCard />

        {/* Carte « Mes objectifs commerciaux » — Manager (Cyrille) uniquement.
            La carte se masque elle-même si l'utilisateur n'est pas manager. */}
        <MyManagerObjectives />

        {/* Carte « Mon Scoring » — barème paramétrable + score pondéré */}
        <MyScoringCard />

        {/* Monthly Target Card */}
        {stats?.monthly_target > 0 && (
          <Card className={`border-2 shadow-card rounded-2xl ${
            stats?.target_progress >= 100 
              ? 'border-emerald-500 bg-emerald-50' 
              : stats?.is_on_track 
                ? 'border-[#2E7D32] bg-[#E8F5E9]' 
                : 'border-amber-400 bg-amber-50'
          }`} data-testid="target-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Target size={22} weight="duotone" className={`${
                    stats?.target_progress >= 100 
                      ? 'text-emerald-600' 
                      : stats?.is_on_track 
                        ? 'text-[#2E7D32]' 
                        : 'text-amber-600'
                  }`} />
                  <h3 className="font-display font-semibold text-gray-900">
                    {language === 'fr' ? 'Objectif du mois' : 'Monthly Target'}
                  </h3>
                </div>
                <Badge className={`font-medium ${
                  stats?.target_progress >= 100 
                    ? 'bg-emerald-500 text-white' 
                    : stats?.is_on_track 
                      ? 'bg-[#2E7D32] text-white' 
                      : 'bg-amber-500 text-white'
                }`}>
                  {stats?.target_progress >= 100 
                    ? (language === 'fr' ? 'Atteint!' : 'Achieved!') 
                    : stats?.is_on_track 
                      ? (language === 'fr' ? 'En bonne voie' : 'On Track')
                      : (language === 'fr' ? 'Effort requis' : 'Effort Needed')}
                </Badge>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {stats?.total_revenue?.toLocaleString('fr-FR')} / {stats?.monthly_target?.toLocaleString('fr-FR')} FCFA
                  </span>
                  <span className="font-bold text-gray-900">
                    {stats?.target_progress?.toFixed(0)}%
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="relative">
                  <Progress 
                    value={Math.min(stats?.target_progress || 0, 100)} 
                    className={`h-3 rounded-full ${
                      stats?.target_progress >= 100 
                        ? '[&>div]:bg-emerald-500' 
                        : stats?.is_on_track 
                          ? '[&>div]:bg-[#2E7D32]' 
                          : '[&>div]:bg-amber-500'
                    }`}
                  />
                </div>
                
                {/* Milestone badges */}
                <div className="flex justify-between text-xs mt-2">
                  <span className={`px-3 py-1 rounded-full font-medium ${
                    stats?.milestones_reached?.includes(50) 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    50%
                  </span>
                  <span className={`px-2 py-0.5 rounded-full ${
                    stats?.milestones_reached?.includes(75) 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    75%
                  </span>
                  <span className={`px-2 py-0.5 rounded-full ${
                    stats?.milestones_reached?.includes(100) 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    100%
                  </span>
                </div>
              </div>
              
              {/* Next milestone info */}
              {stats?.next_milestone && stats?.amount_to_next_milestone > 0 && (
                <div className="mt-3 p-2 rounded-lg bg-[#C9A086]/10 border border-[#C9A086]/20">
                  <p className="text-sm text-[#A67B5B] text-center font-medium">
                    🎯 {language === 'fr' 
                      ? `Plus que ${stats.amount_to_next_milestone.toLocaleString('fr-FR')} FCFA pour atteindre ${stats.next_milestone}%!`
                      : `Only ${stats.amount_to_next_milestone.toLocaleString('fr-FR')} FCFA to reach ${stats.next_milestone}%!`}
                  </p>
                </div>
              )}
              
              {stats?.target_progress < 100 && (
                <div className="mt-3 pt-3 border-t border-[#C9A086]/20">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5D4037]/60">
                      {language === 'fr' ? 'Jours restants' : 'Days left'}
                    </span>
                    <span className="font-medium">{stats?.days_remaining}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-[#78716C]">
                      {language === 'fr' ? 'Objectif/jour' : 'Daily target'}
                    </span>
                    <span className="font-medium text-[#0EA5E9]">
                      {stats?.daily_target_needed?.toLocaleString('fr-FR')} FCFA
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Supplement Renewal Alerts */}
        {supplementRenewals.total > 0 && (
          <Card 
            className={`border-2 ${supplementRenewals.overdue_count > 0 ? 'border-red-300 bg-red-50' : 'border-purple-300 bg-purple-50'}`}
            data-testid="supplement-renewals-alert"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${supplementRenewals.overdue_count > 0 ? 'bg-red-100' : 'bg-purple-100'}`}>
                  <Pill className={`w-5 h-5 ${supplementRenewals.overdue_count > 0 ? 'text-red-600' : 'text-purple-600'}`} />
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${supplementRenewals.overdue_count > 0 ? 'text-red-800' : 'text-purple-800'}`}>
                    {supplementRenewals.total} {language === 'fr' ? 'renouvellement(s) de cure' : 'cure renewal(s)'}
                  </p>
                  {supplementRenewals.overdue_count > 0 && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {supplementRenewals.overdue_count} {language === 'fr' ? 'en retard' : 'overdue'}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Renewal items (max 3) */}
              <div className="space-y-2 mt-3">
                {supplementRenewals.renewals.slice(0, 3).map((renewal, idx) => (
                  <div 
                    key={`${renewal.sale_id}-${renewal.supplement_id}-${idx}`}
                    className={`p-3 rounded-lg ${renewal.is_overdue ? 'bg-red-100' : 'bg-white'} border ${renewal.is_overdue ? 'border-red-200' : 'border-purple-100'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-sm text-[#0F172A]">{renewal.client_name}</p>
                        <p className="text-xs text-[#78716C]">
                          {renewal.supplement_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={renewal.is_overdue ? "destructive" : "secondary"} className="text-xs">
                            {renewal.is_overdue 
                              ? (language === 'fr' ? 'En retard' : 'Overdue')
                              : formatRenewalDate(renewal.renewal_date)
                            }
                          </Badge>
                          <span className="text-xs text-[#78716C]">
                            {renewal.cure_duration_days} {language === 'fr' ? 'jours de cure' : 'day cure'}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkRenewalDone(renewal.sale_id, renewal.supplement_id, renewal.client_name);
                        }}
                        title={language === 'fr' ? 'Marquer comme fait' : 'Mark as done'}
                      >
                        <CheckCircle className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {supplementRenewals.total > 3 && (
                  <p className="text-xs text-center text-purple-600 pt-2">
                    +{supplementRenewals.total - 3} {language === 'fr' ? 'autres renouvellements' : 'more renewals'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cure Follow-up Reminders Section */}
        <section>
          <CureReminders language={language} />
        </section>

        {/* Follow-up Alert */}
        {((followUps.scheduled_follow_ups?.length || 0) + (followUps.renewal_reminders?.length || 0)) > 0 && (
          <Card 
            className="border-orange-300 bg-orange-50 cursor-pointer hover:shadow-md transition-shadow"
            onClick={onViewClients}
            data-testid="followup-alert"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-orange-800">
                    {(followUps.scheduled_follow_ups?.length || 0) + (followUps.renewal_reminders?.length || 0)} {language === 'fr' ? 'client(s) à relancer' : 'client(s) to follow up'}
                  </p>
                  <p className="text-sm text-orange-600">
                    {language === 'fr' ? 'Cliquez pour voir la liste' : 'Click to view list'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Nouveau conseil — bouton principal unifié */}
        <motion.div
          className="grid grid-cols-1 gap-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
        >
          <motion.button
            onClick={onNewDiagnosis}
            className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white h-14 text-base rounded-xl shadow-md font-medium flex items-center justify-center gap-2"
            data-testid="agent-new-diagnosis-btn"
            whileHover={{ scale: 1.02, boxShadow: "0 8px 30px rgba(46, 125, 50, 0.35)" }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Plus className="w-5 h-5" />
            {language === 'fr' ? 'Nouveau conseil' : 'New Consultation'}
          </motion.button>

          {onBabyDiagnosis && (
            <motion.button
              onClick={onBabyDiagnosis}
              className="border-2 border-[#E8B4B8] text-[#C08B8F] hover:bg-[#FDF2F3] h-12 text-sm rounded-xl font-medium flex items-center justify-center gap-2 bg-white"
              data-testid="agent-baby-diagnosis-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Baby className="w-4 h-4" />
              {language === 'fr' ? 'Conseil Bébé' : 'Baby Advice'}
            </motion.button>
          )}
        </motion.div>

        {/* Recent Diagnoses - Animated */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0F172A]">
              {language === 'fr' ? 'Derniers conseils' : 'Recent Consultations'}
            </h2>
          </div>

          {diagnoses.length === 0 ? (
            <Card className="border-gray-200 border-dashed bg-white/50">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {language === 'fr' ? 'Aucun diagnostic ce mois' : 'No diagnoses this month'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <motion.div 
              className="space-y-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {diagnoses.slice(0, 5).map((diagnosis, idx) => (
                <motion.div
                  key={diagnosis.id}
                  variants={cardVariants}
                  whileHover={{ scale: 1.01, x: 4 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Card 
                    className="border-gray-100 bg-white cursor-pointer hover:shadow-lg transition-shadow hover:border-[#2E7D32]/30"
                    onClick={() => onViewDiagnosis(diagnosis)}
                    data-testid={`agent-diagnosis-${idx}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <motion.div 
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          diagnosis.has_sale 
                            ? 'bg-gradient-to-br from-green-400 to-green-500' 
                            : 'bg-gradient-to-br from-[#2E7D32] to-[#1B5E20]'
                        }`}
                        whileHover={{ rotate: 5, scale: 1.1 }}
                      >
                        <span className="font-bold text-white">
                          {diagnosis.skin_score}
                        </span>
                      </motion.div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {diagnosis.client_name || (language === 'fr' ? 'Client' : 'Client')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(diagnosis.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {diagnosis.has_sale ? (
                          <Badge className="bg-green-100 text-green-700 border-0">
                            {language === 'fr' ? 'Vendu' : 'Sold'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500 border-gray-200">
                            {language === 'fr' ? 'En attente' : 'Pending'}
                          </Badge>
                        )}
                        <motion.div
                          whileHover={{ x: 3 }}
                          transition={{ type: "spring", stiffness: 400 }}
                        >
                          <ChevronRight className="w-5 h-5 text-[#2E7D32]" />
                        </motion.div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.section>
      </main>
    </div>
  );
};

export default AgentDashboard;
